import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/**
 * One-shot composer drafts, handed between surfaces through the query cache
 * (the same channel as file-record text selections). A producer pushes text
 * keyed by the flow session whose composer should show it, then navigates /
 * nudges that chat open; the composer for that session consumes the draft and
 * the entry is cleared.
 *
 * Keying by session is what keeps the seed from spreading: the composer that
 * claims it holds it for its own lifetime (so its `defaultValue` is stable),
 * while any other session's composer — and any later remount of the same one,
 * including after the user sent the message — finds an empty cache and starts
 * blank.
 *
 * Drafts only ever reach a composer's `defaultValue` / `setMessage` — they
 * are never auto-sent.
 */

const COMPOSER_DRAFT_KEY = "composer-draft";

export const composerDraftQueryKey = (scopeId: string) =>
  [COMPOSER_DRAFT_KEY, scopeId] as const;

export function pushComposerDraft(
  queryClient: QueryClient,
  scopeId: string,
  text: string,
): void {
  if (!scopeId || !text) return;
  queryClient.setQueryData(composerDraftQueryKey(scopeId), text);
}

/**
 * Returns the pending draft for this session once, clearing it after handoff.
 * Subscribes to the cache directly (no queryFn — the data is imperative), so
 * a push into an already-mounted composer still lands.
 */
export function useComposerDraft(
  scopeId: string | undefined,
): string | undefined {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!scopeId) return;
    const queryKey = composerDraftQueryKey(scopeId);

    const consume = () => {
      const value = queryClient.getQueryData<string>(queryKey);
      if (!value) return;
      setDraft(value);
      queryClient.removeQueries({ queryKey });
    };

    // The producer usually pushes before this surface mounts.
    consume();
    return queryClient.getQueryCache().subscribe((event) => {
      const [keyRoot, keyScope] = event.query.queryKey;
      if (keyRoot === COMPOSER_DRAFT_KEY && keyScope === scopeId) consume();
    });
  }, [queryClient, scopeId]);

  return draft;
}
